import { CommonModule } from "@angular/common";
import {
	Component,
	EventEmitter,
	inject,
	Input,
	OnInit,
	Output,
	signal,
	ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { SvgIconComponent } from "angular-svg-icon";
import { AccordionModule } from "primeng/accordion";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { PanelModule } from "primeng/panel";
import { SkeletonModule } from "primeng/skeleton";
import { UsersPrivilegeKey } from "../../../core/models/user";
import {
	PrivilegeKey,
	Publisher,
	PublisherItem,
} from "../../models/publisher";
import { FormatService } from "../../services/format/format.service";
import { UserService } from "../../services/user/user.service";
import { ManagementService } from "../../services/management.service";
import { StorageService } from "../../services/storage/storage.service";
import { TooltipModule } from "primeng/tooltip";

@Component({
    selector: "app-publisher",
    imports: [
        AccordionModule,
        CheckboxModule,
        CardModule,
        CommonModule,
        ButtonModule,
        SvgIconComponent,
        FormsModule,
        SkeletonModule,
        PanelModule,
        TooltipModule,
    ],
    templateUrl: "./publisher.component.html",
    styleUrl: "./publisher.component.scss",
    encapsulation: ViewEncapsulation.None
})
export class PublisherComponent implements OnInit {
	@Output() dialogEvent = new EventEmitter(false);
	@Output() notification = new EventEmitter(true);
	@Input() status: boolean | undefined;
	infoMsg = true;
	errorMsg = false;
	successMsg = false;
	loading = false;
	publishingMsg = false;
	selectedItem: any[] = [];
	publisherItem: Publisher[] = [];
	totalItem = 0;
	errorList: any = [];
	btnLoading = false;
	title: string = $localize`:@@publisher.title:Publisher`;
	items: string = $localize`:@@publisher.items:Items`;
	item: string = $localize`:@@publisher.item:Item`;
	btnPublish: string = $localize`:@@publisher.publish:Publish`;
	created: string = $localize`:@@publisher.created:created`;
	updated: string = $localize`:@@publisher.updated:updated`;
	publishSubmittedCount = signal<number>(0);
	private readonly managementService = inject(ManagementService);
	constructor(
		private router: Router,
		private userService: UserService,
		public formatService: FormatService,
		private storageService: StorageService,
	) {}
	ngOnInit(): void {
		this.displayMessage();
	}

	getPublisherList(enableErrorMsg?: boolean) {
		this.loading = true;
		this.successMsg = false;
		this.errorMsg = enableErrorMsg ? enableErrorMsg : false;
		this.totalItem = 0;
		this.errorList = [];
		this.publisherItem = [];
		this.displayMessage();
		this.managementService.getPendingEvents().subscribe((response) => {
			this.loading = false;
			response.map((item) => {
				const privilegeName = <UsersPrivilegeKey>PrivilegeKey[item.name];
				const access =
					UsersPrivilegeKey[
						privilegeName as unknown as keyof typeof UsersPrivilegeKey
					];
				if (!this.userService.isAccessNone(access)) {
					this.totalItem = this.totalItem + item.events.length;
					item.selectedItems = [];
					item.events.length > 0 &&
						item.events.map((publishEvent: PublisherItem) => {
							(publishEvent.errorCode || publishEvent.errorMessage) &&
								this.errorList.push({
									errorCode: publishEvent.errorCode,
									errorMessage: publishEvent.errorMessage,
								});
							publishEvent.disabled = this.userService.isReadOnlyAccess(access) ? true : false;
							publishEvent.checked = false;							
						});
					//this.toggleSelectAll(item);
					this.publisherItem.push(item);
				}
			});
			if(response && response.length === 0)
				this.notification.emit(false);
		});
	}

	publish() {
		this.successMsg = false;
		this.errorMsg = false;
		this.publishingMsg = true;
		this.btnLoading = true;
		const publishItem = this.getAllPublisherItem();

		this.managementService.publishEvents(publishItem).subscribe((response) => {
			if (response.jobId) {
				setTimeout(() => {
					this.getJobStatus(response.jobId);
				}, 5000);
				this.publishSubmittedCount.set(publishItem.length);
			} else {
				this.errorMsg = true;
				this.btnLoading = false;
			}
		});
	}

	getJobStatus(jobId: number) {
		this.btnLoading = true;
		this.managementService.getJob(jobId).subscribe((response) => {
			if (response.batchStatus === "STARTED") {
				this.publishingMsg = true;
				setTimeout(() => {
					this.getJobStatus(jobId);
				}, 5000);
			} else if (response.batchStatus === "FAILED") {
				this.errorMsg = true;
				this.publishingMsg = false;
				this.getPublisherList(true);
				this.btnLoading = false;
			} else {
				this.publishingMsg = false;
				this.btnLoading = false;
				this.getPublisherList();
				this.successMsg = true;
			}
		});
	}
	schedules: string = $localize`:@@publisher.panel.header.schedule:Schedules`;
	accounts: string = $localize`:@@publisher.panel.header.account:Accounts`;
	users: string = $localize`:@@publisher.panel.header.user:Users`;
	catalogs: string = $localize`:@@publisher.panel.header.catalog:Card Catalog`;
	templates: string = $localize`:@@publisher.panel.header.templates:Templates`;
	store: string = $localize`:@@publisher.panel.header.store:Storefront Configuration`;
	promotions: string = $localize`:@@publisher.panel.header.promotions:Promotions`;
	brand: string = $localize`:@@publisher.panel.header.brand.management:Brand Management`;
	internalOrdering: string = $localize`:@@publisher.panel.header.internal.ordering:Internal Ordering`;
	formatName(label: string) {
		switch (label) {
			case "SCHEDULE":
				return this.schedules;
			case "ACCOUNT":
				return this.accounts;
			case "CATALOG":
				return this.catalogs;
			case "TEMPLATE":
				return this.templates;
			case "STORE":
				return this.store;
			case "PROMOTION":
				return this.promotions;
			case "BRAND":
				return this.brand;
			case "INTERNAL_ORDERING":
				return this.internalOrdering;
			default:
				return this.users;
		}
	}

	close() {
		this.dialogEvent.emit(true);
	}

	toggleSelectAll(group: Publisher) {
		if (group.selectAll) {
			group.selectedItems = group.events.map((item) => {
				if(!item.checked && !item.disabled){
					this.toggleCheck(group, item, item.checked);
				}
				return item.uuid;
			});	
			group.selectAll = true;		
		} else {
			group.selectedItems =  group.events.map((item) => {
				if(!item.disabled){
					this.toggleCheck(group, item, item.checked);
					return;
				} else {
					return item.uuid;
				}
			});
			group.selectAll = false;	
			group.selectedItems = group.selectedItems.filter(item => item);
		}
	}

	toggleCheck(group:Publisher, item: PublisherItem, checked?:boolean) {
		item.checked = !checked ? true : false;
		this.successMsg = false;
		this.errorMsg = false;
		if(Object.keys(item.dependencyMap).length){
			let selectedItem = Object.keys(item.dependencyMap);
			selectedItem.map(selectedRecord => {
				this.publisherItem.map(items => {
					const matchFound = items.events.findIndex(data => data.uuid === selectedRecord);
					if(matchFound !== -1){
						if (!checked) {
						items.events[matchFound].checked = true;
						items.events[matchFound].disabled = true;
						if (!items.selectedItems.includes(selectedRecord)) {
							items.selectedItems = [...items.selectedItems, selectedRecord];
						}
						this.toggleCheck(group, items.events[matchFound]);
						} else {
						const occurrences = items.selectedItems.filter(v => v === selectedRecord).length;
						if (occurrences === 1) {
							items.events[matchFound].checked = false;
							items.events[matchFound].disabled = this.checkDisabled(items.name) ? true : false;
							items.selectedItems = items.selectedItems.filter(v => v !== selectedRecord);
						} else {
							const index = items.selectedItems.indexOf(selectedRecord);
							// drop one occurrence only; item is still required by another dependency
							items.selectedItems = items.selectedItems.filter((_, i) => i !== index);
						}
						this.toggleCheck(group, items.events[matchFound], true);							
						}

					}
					if(items.events.length <= items.selectedItems.length){
						items.selectAll = true;
					} else {
						items.selectAll = false;
					}
				})
			})
		}
		if(group.events.length <= group.selectedItems.length){
			group.selectAll = true;
		} else {
			group.selectAll = false;
		}
	}

	getOverallSelectedCount() {
		return this.publisherItem.reduce((count, item) => count + [...new Set(item.selectedItems)].length, 0);
	}

	getTotalPublisherItem() {
		return (
			this.publisherItem &&
			this.publisherItem.reduce((count, item) => count + [...new Set(item.selectedItems)].length, 0)
		);
	}

	getAllPublisherItem() {
		return this.publisherItem.reduce(
			(selected: any, item) => selected.concat([...new Set(item.selectedItems)].map(value => ({ 'uuid': value }))),
			[],
		);
	}

	routeItem(item: PublisherItem) {
		if (item.eventSource === "ACCOUNT") {
			this.router.navigate(["/accounts/details/", item.eventSourceId]);
		}else if(item.eventSource === "INTERNAL_ORDERING") {
			this.router.navigate(["/internal-orders", item.eventSourceId]);
		} else if (item.eventSource === "CATALOG") {
			this.router.navigate(["/catalog/details/", item.eventSourceId]);
		} else if (item.eventSource === "USER") {
			this.userService.getUserDetail(item.eventSourceId).subscribe((data) => {
				if(data.userType === 'storefront'){
				this.router.navigate(["/users/" + data.userType + "/details/", item.eventSourceId]);
				} else {
					this.router.navigate(["/settings/portal-users/details/", item.eventSourceId]);
				}
			});
		} else if (item.eventSource === "SCHEDULE") {
			this.router.navigate(["/schedules/details/", item.eventSourceId]);
		} else if (item.eventSource === "TEMPLATE") {
			this.router.navigate(["/templates"]);
		} else if (item.eventSource === "CARD_ARRANGEMENT") {
			this.router.navigate(["/storefront/" + item.lowerMerchantId +"/card-display-settings"]);
		} else if (item.eventSource === "OCCASION") {
			this.router.navigate(["/storefront/card-display-settings"]);
		} else if (item.eventSource === "MESSAGE") {
			this.router.navigate([
				"/storefront/" + item.lowerMerchantId + "/"+ item.eventSourceId + "/custom-content",
			]);
		} else if (item.eventSource === "STORE") {
			this.router.navigate(["/storefront/style/", item.eventSourceId]);
		} else if (item.eventSource === "PROMOTION") {
			this.router.navigate(["/promotions"]);
		} else if (item.eventSource === "BRAND" || item.eventSource === "BRAND_CATEGORY") {
			this.router.navigate(["/program-settings/brands"]);
		}
	}
	formatContent(subItem: PublisherItem) {
		return (
			$localize`:@@publisher.display.content:was ${subItem.eventType === "CREATE" ? this.created : this.updated} by ` +
			subItem.createdBy
		);
	}

	displayMessage(){
		if(this.storageService.getItem('publisher')){
			this.infoMsg = false;
		}
	}
	handleMessageClose(){
		this.storageService.setItem('publisher')
		this.infoMsg = false;		
	}

	checkDisabled(item: string): boolean {
		const privilegeName = <UsersPrivilegeKey>PrivilegeKey[item];
		const access =
			UsersPrivilegeKey[
				privilegeName as unknown as keyof typeof UsersPrivilegeKey
			];
		if (this.userService.isReadOnlyAccess(access)) {
			return true;
		}
	return false;
}

	protected readonly UsersPrivilegeKeys = UsersPrivilegeKey;
}
